import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { buildPowerDurationCurve } from '@/lib/analytics-calculations';

interface PowerDurationCurveProps {
  peaks: Array<{ duration_seconds: number; power: number }>;
  comparisonPeaks?: Array<{ duration_seconds: number; power: number }>;
  comparisonLabel?: string;
}

export function PowerDurationCurve({ peaks, comparisonPeaks, comparisonLabel }: PowerDurationCurveProps) {
  const data = useMemo(() => {
    const main = buildPowerDurationCurve(peaks);
    if (!comparisonPeaks?.length) return main;
    const comp = buildPowerDurationCurve(comparisonPeaks);
    const compMap = new Map(comp.map((c) => [c.duration, c.power]));
    return main.map((p) => ({ ...p, comparison: compMap.get(p.duration) }));
  }, [peaks, comparisonPeaks]);

  if (!data.length) {
    return null;
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Power Duration Curve</h3>
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="pdcGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.15} />
                <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="durationLabel" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={40} />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} formatter={(value: number) => [`${value}W`, '']} />
            <Area type="monotone" dataKey="power" stroke="hsl(var(--chart-1))" fill="url(#pdcGrad)" strokeWidth={1.5} dot={false} name="Best Power" />
            {comparisonPeaks?.length && (
              <Area type="monotone" dataKey="comparison" stroke="hsl(var(--chart-4))" fill="none" strokeWidth={1} strokeDasharray="4 2" dot={false} name={comparisonLabel || 'Comparison'} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        {[5, 60, 300, 1200].map((dur) => {
          const point = data.find((p) => p.duration === dur);
          const labels: Record<number, string> = { 5: '5s', 60: '1m', 300: '5m', 1200: '20m' };
          return (
            <div key={dur} className="text-center">
              <div className="text-[10px] text-muted-foreground">{labels[dur]}</div>
              <div className="text-sm font-bold tabular-nums text-foreground">{point ? `${point.power}W` : '—'}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
